import DebugHelper from './DebugHelper.js';
import BackgroundHelper from './BackgroundHelper.js';
import UIStyleHelper from './UIStyleHelper.js';
import VehicleEquipmentSystem from './VehicleEquipmentSystem.js';
import { getVehicleTextureKey, isTankSpritesheetKey } from './VehicleTextureHelper.js';

const HUD_FONT = UIStyleHelper.PIXEL_FONT;

export default class WorldUI {
    constructor(scene) {
        this.scene = scene;
        
        // UI元素容器
        this.elements = {
            hud: {},
            dialog: {}
        };
        
        // UI状态
        this.isDialogActive = false;
        this.isMenuActive = false;
        this.isHudVisible = false;
        
        // 当前对话框数据
        this.currentDialog = {
            title: '',
            messages: [],
            currentIndex: 0,
            callback: null
        };
        
        // 事件处理器
        this.events = new Phaser.Events.EventEmitter();
        
        // 键盘处理器引用
        this.spaceKeyHandler = null;
        this.spaceKey = null;
    }
    
    /**
     * 创建HUD状态栏
     */
    createHUD() {
        const { width } = this.scene.cameras.main;
        
        DebugHelper.log('创建HUD，相机宽度:', width);
        
        // 创建状态栏背景（使用BackgroundHelper）
        this.elements.hud.background = this.createHUDBackground(width);
        
        // 创建头像（先创建占位，后续根据玩家element更新）
        // 检查默认头像是否存在，如果不存在则创建一个占位符
        const defaultAvatarKey = 'player_avatar1';
        if (this.scene.textures.exists(defaultAvatarKey)) {
            this.elements.hud.avatar = this.scene.add.image(25, 25, defaultAvatarKey);
            this.elements.hud.avatar.setDisplaySize(40, 40);
        } else {
            // 如果头像资源不存在，创建一个占位符矩形
            this.elements.hud.avatar = this.scene.add.rectangle(25, 25, 40, 40, 0x666666);
            this.elements.hud.avatar.setStrokeStyle(2, 0x888888);
        }
        this.elements.hud.avatar.setOrigin(0, 0.5);
        
        // 玩家名称（调整位置，为头像留出空间）
        this.elements.hud.playerName = UIStyleHelper.createText(
            this.scene, '', 75, 8,
            { fontSize: '14px', fill: '#ffffff', fontFamily: HUD_FONT, fontStyle: 'bold' }
        );
        
        this.elements.hud.hpText = UIStyleHelper.createText(
            this.scene, '', 75, 24,
            { fontSize: '12px', fill: '#ff6666', fontFamily: HUD_FONT }
        );
        
        this.elements.hud.spText = UIStyleHelper.createText(
            this.scene, '', 75, 38,
            { fontSize: '12px', fill: '#66ccff', fontFamily: HUD_FONT }
        );
        
        this.elements.hud.defenseText = UIStyleHelper.createText(
            this.scene, '', 220, 24,
            { fontSize: '12px', fill: '#66ff66', fontFamily: HUD_FONT }
        );
        
        this.elements.hud.moneyText = UIStyleHelper.createText(
            this.scene, '', width - 100, 24,
            { fontSize: '12px', fill: '#ffd700', fontFamily: HUD_FONT, fontStyle: 'bold' }
        );
        
        // 将所有HUD元素固定在相机上
        Object.values(this.elements.hud).forEach(element => {
            if (element && element.setScrollFactor) {
                element.setScrollFactor(0);
                element.setDepth(1000);
            }
        });
    }
    
    /**
     * 更新HUD位置（窗口大小变化时调用）
     */
    updateHUD() {
        const { width } = this.scene.cameras.main;
        
        // 更新HUD背景宽度
        if (this.elements.hud.background) {
            this.elements.hud.background.destroy();
            this.elements.hud.background = this.createHUDBackground(width);
        }
        
        // 更新金钱显示位置
        if (this.elements.hud.moneyText) {
            this.elements.hud.moneyText.x = width - 100;
        }
    }
    
    /**
     * 创建HUD背景
     * @param {number} width - 宽度
     * @returns {Phaser.GameObjects.Graphics} 背景图形
     */
    createHUDBackground(width) {
        const background = BackgroundHelper.createPanel(
            this.scene,
            0, 0, width, 54,
            {
                fillColor: 0x000000,
                fillAlpha: 0.8,
                borderColor: 0x444444,
                borderWidth: 2
            }
        );
        background.setScrollFactor(0);
        background.setDepth(1000);
        return background;
    }
    
    /**
     * 创建对话框
     */
    createDialogBox() {
        // 如果已存在，先销毁
        if (this.elements.dialog.background) {
            this.elements.dialog.background.destroy();
        }
        if (this.elements.dialog.title) {
            this.elements.dialog.title.destroy();
        }
        if (this.elements.dialog.content) {
            this.elements.dialog.content.destroy();
        }
        if (this.elements.dialog.indicator) {
            this.elements.dialog.indicator.destroy();
        }
        
        const { width, height } = this.scene.cameras.main;
        
        DebugHelper.log('创建对话框，屏幕尺寸:', width, 'x', height);
        
        // 对话框背景（使用BackgroundHelper）
        this.elements.dialog.background = BackgroundHelper.createPanel(
            this.scene,
            0, height - 150, width, 150,
            {
                fillColor: 0x000000,
                fillAlpha: 0.9,
                borderColor: 0x884400,
                borderWidth: 3
            }
        );
        this.elements.dialog.background.setScrollFactor(0);
        this.elements.dialog.background.setDepth(2000);
        
        // 标题（使用UIStyleHelper）
        this.elements.dialog.title = UIStyleHelper.createText(
            this.scene, '', 20, height - 140,
            { fontSize: '16px', fill: '#ff9900', fontFamily: HUD_FONT, fontStyle: 'bold' }
        );
        
        // 内容文本（使用UIStyleHelper）
        this.elements.dialog.content = UIStyleHelper.createText(
            this.scene, '', 30, height - 110,
            { fontSize: '14px', fill: '#ffffff', fontFamily: HUD_FONT, wordWrap: { width: width - 60 } }
        );
        
        // 指示器（继续提示）
        this.elements.dialog.indicator = this.scene.add.text(
            width - 80, height - 40, '▶ 继续', {
                fontSize: '14px',
                fill: '#aaaaaa',
                fontFamily: HUD_FONT
            }
        );
        
        // 将指示器设置为可点击
        this.elements.dialog.indicator.setInteractive({ useHandCursor: true });
        this.elements.dialog.indicator.on('pointerdown', () => {
            if (this.isDialogActive) {
                this.handleDialogInput();
            }
        });
        
        // 将对话框背景设置为可点击（点击对话框任意位置关闭）
        this.elements.dialog.background.setInteractive(
            new Phaser.Geom.Rectangle(0, height - 150, width, 150),
            Phaser.Geom.Rectangle.Contains
        );
        this.elements.dialog.background.on('pointerdown', () => {
            if (this.isDialogActive) {
                this.handleDialogInput();
            }
        });
        
        // 将对话框元素固定在相机上
        Object.values(this.elements.dialog).forEach(element => {
            if (element && element.setScrollFactor) {
                element.setScrollFactor(0);
                element.setDepth(1001);
            }
        });
        
        // 初始隐藏对话框
        this.hideDialogBox();
    }
    
    /**
     * 更新对话框位置（窗口大小变化时调用）
     */
    updateDialogBox() {
        if (this.isDialogActive) {
            // 如果对话框正在显示，重新创建以更新位置
            const currentDialog = this.currentDialog;
            this.createDialogBox();
            if (currentDialog) {
                this.currentDialog = currentDialog;
                this.displayCurrentMessage();
            }
        } else {
            // 如果对话框未显示，只需重新创建
            this.createDialogBox();
        }
    }
    
    /**
     * 绑定键盘事件
     */
    bindKeyboardEvents() {
        // 不在这里创建空格键对象，只监听场景的事件
        this.events.on('spacePressed', () => {
            if (this.isDialogActive) {
                DebugHelper.log('WorldUI: 处理对话框空格键');
                this.handleDialogInput();
            }
        });
        
        DebugHelper.log('WorldUI: 键盘事件绑定完成');
    }
    
    /**
     * 创建状态栏（兼容旧代码）
     */
    createStatusBar() {
        DebugHelper.log('WorldUI: 创建状态栏');
        
        // 先创建UI组件
        this.createHUD();
        this.createDialogBox();
        this.bindKeyboardEvents();
        
        // 显示HUD
        this.showHUD();
        
        // 初始化HUD数据
        if (this.scene.player) {
            this.updateHUD(this.scene.player);
        }
    }
    
    /**
     * 显示消息（单条消息）
     */
    showMessage(message, callback = null) {
        DebugHelper.log('显示消息:', message);
        
        this.showDialog({
            title: '消息',
            content: [message],
            callback: callback
        });
    }
    
    /**
     * 显示对话框
     */
    showDialog(dialogData, messages = [], callback = null) {
        DebugHelper.log('显示对话框:', dialogData);
        
        // 处理不同参数格式
        if (typeof dialogData === 'string') {
            // 旧格式
            this.currentDialog = {
                title: dialogData,
                messages: Array.isArray(messages) ? messages : [messages],
                currentIndex: 0,
                callback: callback
            };
        } else if (typeof dialogData === 'object') {
            // 新格式
            this.currentDialog = {
                title: dialogData.title || '对话',
                messages: Array.isArray(dialogData.content) ? dialogData.content : [dialogData.content],
                currentIndex: 0,
                callback: dialogData.callback || callback
            };
        } else {
            DebugHelper.error('无效的对话框数据:', dialogData);
            return;
        }
        
        // 显示对话框
        this.isDialogActive = true;
        this.showDialogBox();
        
        // 通知场景
        this.events.emit('dialogOpen');
        
        // 显示第一条消息
        this.displayCurrentMessage();
    }
    
    /**
     * 显示对话框UI
     */
    showDialogBox() {
        Object.values(this.elements.dialog).forEach(element => {
            if (element.setVisible) {
                element.setVisible(true);
            }
        });
    }
    
    /**
     * 隐藏对话框UI
     */
    hideDialogBox() {
        Object.values(this.elements.dialog).forEach(element => {
            if (element.setVisible) {
                element.setVisible(false);
            }
        });
    }
    
    /**
     * 隐藏当前对话框
     */
    hideDialog() {
        if (!this.isDialogActive) return;
        
        DebugHelper.log('隐藏对话框');
        
        this.hideDialogBox();
        this.isDialogActive = false;
        
        // 执行回调
        if (this.currentDialog.callback && typeof this.currentDialog.callback === 'function') {
            this.currentDialog.callback();
        }
        
        // 通知场景
        this.events.emit('dialogClose');
    }
    
    /**
     * 处理对话框输入
     */
    handleDialogInput() {
        DebugHelper.log('处理对话框输入，当前索引:', this.currentDialog.currentIndex);
        
        if (this.currentDialog.currentIndex < this.currentDialog.messages.length - 1) {
            // 显示下一条消息
            this.currentDialog.currentIndex++;
            this.displayCurrentMessage();
        } else {
            // 关闭对话框
            this.hideDialog();
        }
    }
    
    
    /**
     * 显示当前消息
     */
    displayCurrentMessage() {
        const dialog = this.elements.dialog;
        const currentDialog = this.currentDialog;
        
        if (currentDialog.currentIndex < currentDialog.messages.length) {
            // 更新标题
            dialog.title.setText(currentDialog.title);
            
            // 更新内容
            const message = currentDialog.messages[currentDialog.currentIndex];
            dialog.content.setText(message);
            
            // 更新指示器
            if (currentDialog.currentIndex < currentDialog.messages.length - 1) {
                dialog.indicator.setText('▶ 下一句');
            } else {
                dialog.indicator.setText('▶ 关闭');
            }
        }
    }
    
    /**
     * 根据玩家element获取头像key
     */
    getAvatarKey(element) {
        // 将player1/player2/player3映射到对应的头像key
        if (element === 'player1') {
            return 'player_avatar1';
        } else if (element === 'player2') {
            return 'player_avatar2';
        } else if (element === 'player3') {
            return 'player_avatar3';
        }
        // 默认返回player1的头像
        return 'player_avatar1';
    }
    
    /**
     * 更新HUD显示的数据
     */
    updateHUD(playerData) {
        if (!playerData) return;
        
        // 更新所有HUD元素
        const elements = this.elements.hud;
        
        // 判断是否在战车中
        const inVehicle = playerData.inVehicle && playerData.vehicle;
        
        // 更新头像（在战车中时显示战车头像，否则显示玩家头像）
        if (elements.avatar) {
            let avatarKey;
            if (inVehicle && playerData.vehicle) {
                // 战车头像：使用精灵图 key（兼容旧存档 战车N）
                avatarKey = getVehicleTextureKey(playerData.vehicle);
            } else if (playerData.element) {
                // 玩家头像：使用getAvatarKey方法映射
                avatarKey = this.getAvatarKey(playerData.element);
            }
            
            if (avatarKey) {
                // 检查资源是否存在
                if (this.scene.textures.exists(avatarKey)) {
                    // 如果当前是矩形占位符，需要替换为图片
                    if (elements.avatar.setTexture === undefined) {
                        // 说明是Rectangle，需要替换
                        const x = elements.avatar.x;
                        const y = elements.avatar.y;
                        elements.avatar.destroy();
                        elements.avatar = this.scene.add.image(x, y, avatarKey);
                        elements.avatar.setDisplaySize(40, 40);
                        elements.avatar.setOrigin(0, 0.5);
                        elements.avatar.setScrollFactor(0);
                        elements.avatar.setDepth(1000);
                    } else {
                        // 如果已经是图片，直接更新纹理
                        elements.avatar.setTexture(avatarKey);
                        if (isTankSpritesheetKey(avatarKey) && elements.avatar.setFrame) {
                            elements.avatar.setFrame(0);
                        }
                    }
                } else {
                    DebugHelper.warn('头像资源不存在:', avatarKey);
                    // 如果资源不存在且当前不是占位符，创建占位符
                    if (elements.avatar.setTexture !== undefined) {
                        // 说明是Image，需要替换为Rectangle
                        const x = elements.avatar.x;
                        const y = elements.avatar.y;
                        elements.avatar.destroy();
                        elements.avatar = this.scene.add.rectangle(x, y, 40, 40, 0x666666);
                        elements.avatar.setStrokeStyle(2, 0x888888);
                        elements.avatar.setOrigin(0, 0.5);
                        elements.avatar.setScrollFactor(0);
                        elements.avatar.setDepth(1000);
                    }
                }
            }
        }
        
        // 更新名称（在战车中时显示战车名称，否则显示玩家名称）
        if (elements.playerName) {
            if (inVehicle && playerData.vehicle.name) {
                elements.playerName.setText(playerData.vehicle.name || '');
            } else {
                elements.playerName.setText(playerData.name || '');
            }
        }
        
        // HP / SP（战车）显示
        if (elements.hpText) {
            if (inVehicle && playerData.vehicle) {
                if (!playerData.vehicle.maxSp && playerData.vehicle.equipment) {
                    playerData.vehicle.maxSp = VehicleEquipmentSystem.calculateMaxSp(playerData.vehicle);
                }
                const vehicleSp = playerData.vehicle.sp || 0;
                const vehicleMaxSp = playerData.vehicle.maxSp || 0;
                elements.hpText.setText(`SP ${vehicleSp}/${vehicleMaxSp}`);
            } else {
                elements.hpText.setText(`HP ${playerData.hp || 0}/${playerData.maxHp || 0}`);
            }
        }

        // SP（人类）/ CP（战车）显示
        if (elements.spText) {
            if (inVehicle && playerData.vehicle) {
                if (!playerData.vehicle.maxCorrosionLayer && playerData.vehicle.equipment) {
                    playerData.vehicle.maxCorrosionLayer = VehicleEquipmentSystem.calculateMaxCorrosionLayer(playerData.vehicle);
                }
                const vehicleCp = playerData.vehicle.corrosionLayer || 0;
                const vehicleMaxCp = playerData.vehicle.maxCorrosionLayer || 0;
                elements.spText.setText(`CP ${vehicleCp}/${vehicleMaxCp}`);
            } else {
                const cost = playerData.cost ?? 0;
                const maxCost = playerData.maxCost ?? 0;
                elements.spText.setText(`SP ${cost}/${maxCost}`);
            }
        }
        
        if (elements.defenseText) {
            if (inVehicle && playerData.vehicle) {
                elements.defenseText.setText('');
            } else {
                elements.defenseText.setText(`防 ${playerData.defense || 0}`);
            }
        }
        
        // 金钱显示（无论是否在战车中都显示玩家金钱）
        if (elements.moneyText) {
            const money = (playerData.money !== undefined && playerData.money !== null) ? playerData.money : 0;
            elements.moneyText.setText(`金钱: ${money} G`);
        }
    }
    
    /**
     * 显示HUD
     */
    showHUD() {
        Object.values(this.elements.hud).forEach(element => {
            if (element.setVisible) {
                element.setVisible(true);
            }
        });
        this.isHudVisible = true;
    }
    
    /**
     * 更新UI（游戏主循环调用）
     */
    update() {
        // 可以在这里添加UI动画或效果
    }
    
    /**
     * 销毁所有UI组件
     */
    destroy() {
        // 移除事件监听器
        if (this.spaceKey) {
            if (this.spaceKeyHandler) {
                this.spaceKey.off('down', this.spaceKeyHandler);
            }
            this.spaceKey = null;
        }
        
        // 销毁HUD
        Object.values(this.elements.hud).forEach(element => {
            if (element.destroy) {
                element.destroy();
            }
        });
        this.elements.hud = {};
        
        // 销毁对话框
        Object.values(this.elements.dialog).forEach(element => {
            if (element.destroy) {
                element.destroy();
            }
        });
        this.elements.dialog = {};
        
        // 移除事件监听
        this.events.removeAllListeners();
    }
}